import { IcConfirmedBold } from '@assets/icons/24x24';
import {
  Checkmark,
  createListCollection,
  HStack,
  Icon,
  Listbox,
  Separator,
  useListboxItemContext,
  VStack,
} from '@chakra-ui/react';
import { Dispatch, SetStateAction } from 'react';
import { useIntl } from 'react-intl';

import { CameraLiteConfiguration, SubCameraLiteConfiguration } from '@/types/sensor/sensors';

type Props = {
  availableCameras: CameraLiteConfiguration[];
  setSelectedSubCameras: Dispatch<SetStateAction<SubCameraLiteConfiguration[]>>;
};

type CollectionItem = {
  label: string | null;
  value: string;
  parent: string;
};

const ListboxItemCheckmark = () => {
  const itemState = useListboxItemContext();
  return (
    <Checkmark checked={itemState.selected} disabled={itemState.disabled} width="24px" height="24px">
      <Icon as={IcConfirmedBold} />
    </Checkmark>
  );
};

export default function VideoSelector({ availableCameras, setSelectedSubCameras }: Props) {
  const { formatMessage } = useIntl();

  const collection = createListCollection({
    items: availableCameras.flatMap((ac) =>
      ac.subCameras.map((asc): CollectionItem => ({ label: asc.name, value: asc.uniqueKey, parent: ac.uniqueKey })),
    ),
    groupBy: (item) => item.parent,
  });

  const handleValueChange = (values: string[]) => {
    const selection = availableCameras.flatMap((ac) => ac.subCameras).filter((asc) => values.includes(asc.uniqueKey));
    setSelectedSubCameras(selection);
  };

  return (
    <Listbox.Root
      collection={collection}
      selectionMode="multiple"
      gap={0}
      onValueChange={(event) => handleValueChange(event.value)}
      variant="plain"
    >
      <Listbox.Content>
        <VStack
          gap={3}
          paddingTop={2}
          overflow="hidden"
          overflowY="auto"
          className="map-panel-scrollbar"
          separator={<Separator size="md" borderColor="neutral.900" />}
        >
          {collection.group().map(([parentKey, items]) => (
            <Listbox.ItemGroup key={parentKey}>
              <HStack width="450px" alignItems="top" paddingX={2} justifyContent="space-between">
                <Listbox.ItemGroupLabel width="200px" padding={0} overflow="hidden" wordWrap="break-word">
                  {availableCameras.find((ac) => ac.uniqueKey === parentKey)?.name ??
                    formatMessage({ id: 'videoExport.deletedCam' })}
                </Listbox.ItemGroupLabel>
                <VStack gap={2} alignItems="baseline">
                  {items.map((item) => (
                    <Listbox.Item item={item} key={item.value} padding={0} width="200px">
                      <ListboxItemCheckmark />
                      <Listbox.ItemText overflow="hidden" wordWrap="break-word">
                        {item.label ?? formatMessage({ id: 'videoExport.deletedSubCam' })}
                      </Listbox.ItemText>
                    </Listbox.Item>
                  ))}
                </VStack>
              </HStack>
            </Listbox.ItemGroup>
          ))}
        </VStack>
      </Listbox.Content>
    </Listbox.Root>
  );
}
